import { Injectable } from '@angular/core';
import {
    DeviceManagementConfigurationPolicy,
    DeviceManagementConfigurationPolicyAssignment,
    DeviceManagementConfigurationSetting,
} from '@microsoft/microsoft-graph-types-beta';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { catchError, distinct, last, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import * as actions from './actions';
import { TenantAjaxService } from 'projects/angular-clarity/src/app/services/ajax/tenant-ajax.service';
import { retry } from 'projects/angular-clarity/src/app/pipes/retry.pipe';

interface AssignmentsBody {
    assignments: DeviceManagementConfigurationPolicyAssignment[];
}

@Injectable()
export class DeviceConfigurationPoliciesEffects {
    getAssignments(_tenant: string, id: string) {
        return this.ajax.get<{ value: DeviceManagementConfigurationPolicyAssignment[] }>(
            _tenant,
            `/api/microsoft/graph/deviceManagement/configurationPolicies/${id}/assignments`,
        );
    }

    getSettings(_tenant: string, id: string) {
        return this.ajax.get<{ value: DeviceManagementConfigurationSetting[] }>(
            _tenant,
            `/api/microsoft/graph/deviceManagement/configurationPolicies/${id}/settings`,
        );
    }
    loadConfigurationPolicy$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.loadDeviceConfigurationPolicies),
            distinct((action) => action._tenant),
            mergeMap(({ _tenant }) =>
                this.get(_tenant).pipe(
                    retry(3000, 2, 'Configuration Policy policy timeout'),
                    last(),
                    map((data: any) => {
                        return actions.loadDeviceConfigurationPoliciesSuccess({ _tenant, data });
                    }),
                    catchError((error) => of(actions.loadDeviceConfigurationPoliciesFailure({ _tenant, error }))),
                ),
            ),
        ),
    );

    getOneConfigurationPolicy$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.getOneConfigurationPolicy),
            mergeMap(({ _tenant, id }) =>
                this.get(_tenant).pipe(
                    retry(3000, 1, 'Configuration Policy policy timeout'),
                    last(),
                    map((data: any) => {
                        return actions.getOneConfigurationPolicySuccess({ _tenant, data });
                    }),
                    catchError((error) => of(actions.getOneConfigurationPolicyFailure({ _tenant, error }))),
                ),
            ),
        ),
    );

    get(_tenant: string): Observable<DeviceManagementConfigurationPolicy[]> {
        const fetchPolicyData = (
            policy: DeviceManagementConfigurationPolicy,
        ): Observable<DeviceManagementConfigurationPolicy> => {
            const assignments$ = this.ajax.get<{ value: any[] }>(
                _tenant,
                `/api/microsoft/graph/deviceManagement/configurationPolicies/${policy.id}/assignments`,
            );

            const settings$ = this.ajax.get<{ value: any[] }>(
                _tenant,
                `/api/microsoft/graph/deviceManagement/configurationPolicies/${policy.id}/settings`,
            );

            return forkJoin([assignments$, settings$]).pipe(
                map(([assignments, settings]) => ({
                    ...policy,
                    assignments: assignments.value,
                    settings: settings.value,
                })),
            );
        };

        return this.ajax
            .get<{
                value: DeviceManagementConfigurationPolicy[];
            }>(_tenant, '/api/microsoft/graph/deviceManagement/configurationPolicies')
            .pipe(
                switchMap((res) => {
                    const policies = res.value || [];
                    return policies.length ? combineLatest(policies.map(fetchPolicyData)) : of([]);
                }),
            );
    }

    getOne(_tenant: string, id: string): Observable<DeviceManagementConfigurationPolicy[]> {
        const fetchPolicyData = (
            policy: DeviceManagementConfigurationPolicy,
        ): Observable<DeviceManagementConfigurationPolicy> => {
            return forkJoin([this.getAssignments(_tenant, id), this.getSettings(_tenant, id)]).pipe(
                map(([assignments, settings]) => ({
                    ...policy,
                    assignments: assignments.value,
                    settings: settings.value,
                })),
            );
        };

        return this.ajax
            .get<{
                value: DeviceManagementConfigurationPolicy[];
            }>(_tenant, '/api/microsoft/graph/deviceManagement/configurationPolicies')
            .pipe(
                switchMap((res) => {
                    const policies = res.value || [];
                    return policies.length ? combineLatest(policies.map(fetchPolicyData)) : of([]);
                }),
            );
    }

    createConfigurationPolicy$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.createConfigurationPolicy),
            mergeMap(({ _tenant, policy, assignments }) =>
                this.create(_tenant, policy, assignments).pipe(
                    tap((createdPolicy) =>
                        this.store.dispatch(actions.getOneConfigurationPolicy({ _tenant, id: createdPolicy.id })),
                    ),
                    map((policy: DeviceManagementConfigurationPolicy) =>
                        actions.createConfigurationPolicySuccess({ _tenant, policy }),
                    ),
                    catchError((error) => of(actions.createConfigurationPolicyFailure({ _tenant, error }))),
                ),
            ),
        ),
    );

    create(
        _tenant: string,
        policy: Partial<DeviceManagementConfigurationPolicy>,
        assignments: DeviceManagementConfigurationPolicyAssignment[],
    ): Observable<DeviceManagementConfigurationPolicy> {
        return this.createConfigurationPolicy(_tenant, policy).pipe(
            switchMap((createdPolicy) =>
                this.createPolicyAssignments(_tenant, createdPolicy.id, assignments).pipe(map((_) => createdPolicy)),
            ),
        );
    }

    createConfigurationPolicy(_tenant: string, policy: Partial<DeviceManagementConfigurationPolicy>) {
        return this.ajax.post<DeviceManagementConfigurationPolicy>(
            _tenant,
            '/api/microsoft/graph/deviceManagement/configurationPolicies',
            policy,
        );
    }

    createPolicyAssignments(
        _tenant: string,
        policyId: string,
        assignments: DeviceManagementConfigurationPolicyAssignment[],
    ) {
        const body: AssignmentsBody = {
            assignments: assignments.map((res) => ({ ...res, id: policyId })),
        };

        return this.ajax.post<DeviceManagementConfigurationPolicy>(
            _tenant,
            `/api/microsoft/graph/deviceManagement/configurationPolicies/${policyId}/assign`,
            body,
        );
    }

    // updatePolicySettings(_tenant: string, policyId: string, settings: DeviceManagementConfigurationSetting[]) {
    //     const requestToUpdateSetting$ = (setting: DeviceManagementConfigurationSetting) =>
    //         this.ajax.patch<DeviceManagementConfigurationPolicy>(
    //             _tenant,
    //             `/api/microsoft/graph/deviceManagement/configurationPolicies/${policyId}/settings/${setting.id}`,
    //             setting,
    //         );

    //     return combineLatest(settings.map((setting) => requestToUpdateSetting$(setting)));
    // }

    updateConfigurationPolicy$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.updateConfigurationPolicy),
            mergeMap(({ _tenant, policy, assignments, id }) =>
                // we are not able to update so we delete the old policy then we create a new one
                this.update({ _tenant, id, policy, assignments }).pipe(
                    tap((newPolicy) =>
                        this.store.dispatch(actions.getOneConfigurationPolicy({ _tenant, id: newPolicy.id })),
                    ),
                    // remove old policy
                    map((_) => actions.deleteConfigurationPolicySuccess({ _tenant, id })),
                    catchError((error) => of(actions.updateConfigurationPolicyFailure({ _tenant, error }))),
                ),
            ),
        ),
    );

    update(body: {
        id;
        _tenant: string;
        policy: DeviceManagementConfigurationPolicy;
        assignments: DeviceManagementConfigurationPolicyAssignment[];
    }) {
        // there is no way for updating settings so we simple delete the policy then create a new one
        const request$ = this.ajax.delete<DeviceManagementConfigurationPolicy>(
            body._tenant,
            '/api/microsoft/graph/deviceManagement/configurationPolicies/' + body.id,
        );

        return request$.pipe(switchMap(() => this.create(body._tenant, body.policy, body.assignments)));
    }

    deleteConfigurationPolicy$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.deleteConfigurationPolicy),
            mergeMap(({ _tenant, id }) =>
                this.ajax
                    .delete<string>(_tenant, '/api/microsoft/graph/deviceManagement/configurationPolicies/' + id)
                    .pipe(
                        retry(3000, 1, 'delete configuration policy timeout'),
                        last(),
                        map(() => actions.deleteConfigurationPolicySuccess({ _tenant, id })),
                        catchError((error) => of(actions.deleteConfigurationPolicyFailure({ _tenant, error }))),
                    ),
            ),
        ),
    );

    constructor(
        private actions$: Actions,
        private ajax: TenantAjaxService,
        private store: Store<any>,
    ) {}
}
